import { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import { update } from "../api/API";

import {
  getCurrentUser,
  isLoggedIn,
  getToken,
  updateUser,
} from "../utility/UserUtils";

import LoadingSpinner from "../components/Generic/LoadingSpinner";
import ProfileHeader from "../components/Profile/ProfileHeader";
import ProfileSummaryCard from "../components/Profile/ProfileSummaryCard";
import ExperienceModal from "../components/Profile/ExperienceModal";
import ProfileAboutSection from "../components/Profile/ProfileAboutSection";
import ProfileContactSection from "../components/Profile/ProfileContactSection";
import ProfileEducationSection from "../components/Profile/ProfileEducationSection";
import ProfileCohortsSection from "../components/Profile/ProfileCohortSection";
import ProfileEventsSection from "../components/Profile/ProfileEventsSection";
import ProfileExperienceSection from "../components/Profile/ProfileExperienceSection";

import "../styles/MemberProfile.css";

const API_BASE = process.env.REACT_APP_API_URL;

const emptyAddress = { street: "", city: "", state: "", zip: "", country: "" };

const emptyEducation = {
  school: "",
  degree: "",
  field: "",
  startYear: "",
  endYear: "",
};

const ProfilePage = () => {
  const [user, setUser] = useState(null);
  const [cohorts, setCohorts] = useState([]);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);

  // FORM STATE
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    dob: "",
    avatar: "",
    address: emptyAddress,
    experiences: [],
    education: emptyEducation,

    industries: "",
    skills: [],
    linkedin: "",
    website: "",
    bio: "",
    socialLinks: { twitter: "", facebook: "", instagram: "" },
    cohort: null,
    canGiveReferral: true,
  });

  // EXPERIENCE MODAL
  const [showExpModal, setShowExpModal] = useState(false);
  const [expForm, setExpForm] = useState({
    companyName: "",
    jobTitle: "",
    industry: "",
    startDate: "",
    endDate: "",
    current: false,
    yearsInTech: "",
  });

  // LOAD USER
  useEffect(() => {
    if (isLoggedIn()) {
      const currentUser = getCurrentUser();
      setUser(currentUser);
      resetForm(currentUser);
    }
  }, []);

  // LOAD COHORTS
  useEffect(() => {
    const fetchCohorts = async () => {
      try {
        const res = await axios.get(`${API_BASE}/cohorts?limit=100`);
        setCohorts(res.data.docs);
      } catch (err) {
        console.error("Failed to fetch cohorts:", err);
      }
    };
    fetchCohorts();
  }, []);

  // RESET FORM WITH USER DATA
  const resetForm = (userData) => {
    setForm({
      firstName: userData.firstName || "",
      lastName: userData.lastName || "",
      email: userData.email || "",
      phone: userData.phone || "",
      avatar: userData.avatar || "",
      dob: userData.dob
        ? new Date(userData.dob).toISOString().split("T")[0]
        : "",
      address: userData.address || emptyAddress,
      experiences:
        userData.experiences?.length > 0 ? [userData.experiences[0]] : [],
      education: userData.education || emptyEducation,

      industries: userData.industries || "",
      skills: userData.skills?.map((s) => s.skill) || [],
      linkedin: userData.linkedin || "",
      website: userData.website || "",
      bio: userData.bio || "",
      socialLinks: userData.socialLinks || {
        twitter: "",
        facebook: "",
        instagram: "",
      },
      cohort: userData.cohort
        ? {
            id: userData.cohort.id,
            title: userData.cohort.title,
            range: userData.cohort.range,
          }
        : null,
      canGiveReferral: userData.canGiveReferral ?? true,
    });
  };

  // VALIDATION
  const validateForm = () => {
    const newErrors = {};
    const safeTrim = (value) => (value ? value.trim() : "");

    if (!safeTrim(form.firstName))
      newErrors.firstName = "First name is required.";
    if (!safeTrim(form.lastName)) newErrors.lastName = "Last name is required.";
    if (!safeTrim(form.phone)) newErrors.phone = "Phone number is required.";
    if (!form.dob) newErrors.dob = "Date of birth is required.";
    if (!safeTrim(form.address.city)) newErrors.city = "City is required.";
    if (!safeTrim(form.address.zip)) newErrors.zip = "ZIP code is required.";
    if (!form.cohort?.id) newErrors.cohort = "Please select a cohort.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // AVATAR UPLOAD
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const uploadProfilePhoto = async (fileToUpload) => {
    const token = getToken();
    if (!token) return;

    const formData = new FormData();
    formData.append(
      "_payload",
      JSON.stringify({ alt: fileToUpload.name, prefix: "profile-photos" })
    );
    formData.append("file", fileToUpload);

    const res = await axios.post(`${API_BASE}/profile-photos`, formData, {
      headers: { Authorization: `Bearer ${token}` },
    });

    return res.data;
  };

  // SAVE PROFILE
  const handleSave = async () => {
    if (!user) return;

    if (!validateForm()) {
      toast.error("Please fill all required fields correctly.");
      return;
    }

    setLoading(true);
    try {
      let avatarId = user.avatar?.id || user.avatar;

      if (file) {
        const uploadRes = await uploadProfilePhoto(file);
        avatarId = uploadRes.doc.id;
      }

      const payloadSkills = form.skills.map((skill) => ({ skill }));

      const formData = new FormData();
      formData.append(
        "_payload",
        JSON.stringify({
          ...form,
          dob: form.dob ? new Date(form.dob).toISOString() : null,
          avatar: avatarId,
          skills: payloadSkills,
          cohort: form.cohort?.id || null,
          address: form.address,
        })
      );

      const token = getToken();
      const res = await update(`/members/${user.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const updatedUser = res.doc;
      updateUser(updatedUser);
      setUser(updatedUser);
      resetForm(updatedUser);

      setFile(null);
      setPreview(null);

      toast.success("Profile updated successfully!");
      setIsEditing(false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  // CANCEL PROFILE EDIT
  const handleCancel = () => {
    if (user) resetForm(user);
    setFile(null);
    setPreview(null);
    setIsEditing(false);
    setErrors({});
  };

  // UPDATE SECTION (EXPERIENCE, EDUCATION, ABOUT, CONTACT)
  const updateProfileSection = async (updatedFields) => {
    if (!user) return;

    setLoading(true);
    try {
      const token = getToken();
      const formData = new FormData();

      const safePayload = {
        id: user.id,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        role: user.role?.id || user.role,
        cohort: form.cohort?.id || form.cohort,
        canGiveReferral: form.canGiveReferral,
        ...updatedFields,
      };

      formData.append("_payload", JSON.stringify(safePayload));

      const res = await update(`/members/${user.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const updatedUser = res.doc;
      updateUser(updatedUser);
      setUser(updatedUser);
      resetForm(updatedUser);

      toast.success("Profile updated!");
    } catch (err) {
      console.error(err);
      toast.error("Update failed");
    } finally {
      setLoading(false);
    }
  };

  // EXPERIENCE MODAL
  const openExperienceModal = () => {
    const exp = form.experiences[0];

    if (exp) {
      setExpForm({
        companyName: exp.companyName || "",
        jobTitle: exp.jobTitle || "",
        industry: exp.industry || "",
        startDate: exp.startDate ? exp.startDate.split("T")[0] : "",
        endDate: exp.endDate ? exp.endDate.split("T")[0] : "",
        current: exp.currentlyWorking || false,
        yearsInTech: exp.years ? String(exp.years) : "",
      });
    } else {
      setExpForm({
        companyName: "",
        jobTitle: "",
        industry: "",
        startDate: "",
        endDate: "",
        current: false,
        yearsInTech: "",
      });
    }

    setShowExpModal(true);
  };

  const handleExperienceSave = async () => {
    const payload = {
      companyName: expForm.companyName,
      jobTitle: expForm.jobTitle,
      industry: expForm.industry,
      startDate: expForm.startDate || null,
      endDate: expForm.current ? null : expForm.endDate || null,
      currentlyWorking: expForm.current,
      years: expForm.yearsInTech ? Number(expForm.yearsInTech) : 0,
    };

    // Update UI state
    setForm((prev) => ({
      ...prev,
      experiences: [payload],
      canGiveReferral: expForm.canGiveReferral ?? prev.canGiveReferral,
    }));

    await updateProfileSection({
      experiences: [payload],
      canGiveReferral: expForm.canGiveReferral ?? true,
    });

    setShowExpModal(false);
  };

  const removeExperience = () => {
    setForm((prev) => ({
      ...prev,
      experiences: [],
    }));
  };

  // EXPERIENCE SUMMARY
  const primaryExperience = form.experiences[0] || null;

  const hasExperience =
    form.experiences.length > 0 &&
    (form.experiences[0].jobTitle ||
      form.experiences[0].companyName ||
      form.experiences[0].industry);

  // AVATAR URL
  const getAvatarUrl = () => {
    if (preview) return preview;
    if (user?.avatar?.url) return user.avatar.url;
    if (typeof user?.avatar === "string")
      return `${API_BASE}/media/${user.avatar}`;
    return "/assets/images/default-avatar.png";
  };

  return (
    <>
      {loading && (
        <div className="loading-overlay">
          <LoadingSpinner text="Updating Profile..." />
        </div>
      )}

      {showExpModal && (
        <ExperienceModal
          show={showExpModal}
          onHide={() => setShowExpModal(false)}
          expForm={expForm}
          setExpForm={setExpForm}
          onSave={handleExperienceSave}
          updateProfileSection={updateProfileSection}
        />
      )}

      <div
        className={`profile-wrapper bg-primary ${loading ? "page-blur" : ""}`}
      >
        <ProfileHeader avatarUrl={getAvatarUrl()} />

        <ProfileSummaryCard
          avatarUrl={getAvatarUrl()}
          onFileChange={handleFileChange}
          form={form}
          setForm={setForm}
          errors={errors}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          primaryExperience={primaryExperience}
          loading={loading}
          onSave={handleSave}
          onCancel={handleCancel}
        />

        <Container className="profile-content">
          <Row className="profile-grid">
            <Col md={6} className="mb-4">
              <ProfileAboutSection
                form={form}
                setForm={setForm}
                isEditing={isEditing}
                cohorts={cohorts}
                errors={errors}
                updateProfileSection={updateProfileSection}
              />
            </Col>

            <Col md={6} className="mb-4">
              <ProfileContactSection
                form={form}
                setForm={setForm}
                isEditing={isEditing}
                errors={errors}
                updateProfileSection={updateProfileSection}
              />
            </Col>

            <Col md={6} className="mb-4">
              <ProfileExperienceSection
                experiences={form.experiences}
                hasExperience={hasExperience}
                onAdd={openExperienceModal}
                onEdit={openExperienceModal}
                onRemove={removeExperience}
                updateProfileSection={updateProfileSection}
              />
            </Col>

            <Col md={6} className="mb-4">
              <ProfileEducationSection
                form={form}
                setForm={setForm}
                updateProfileSection={updateProfileSection}
              />
            </Col>

            <Col md={6} className="mb-4">
              <ProfileCohortsSection
                cohort={form.cohort}
                cohorts={cohorts}
                updateProfileSection={updateProfileSection}
              />
            </Col>

            <Col md={6} className="mb-4">
              <ProfileEventsSection />
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default ProfilePage;
